import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { P } from "@/lib/routes";
import NotFound from "@/pages/NotFound";
import Article, { Articles } from "@/pages/Article";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/i18n";
import Landing from "@/pages/Landing";
import Admin from "@/pages/Admin";
import Operator from "@/pages/Operator";
import AdminResetPassword from "@/pages/AdminResetPassword";
import Rodo from "@/pages/Rodo";
import Payment from "@/pages/Payment";
import DocumentGenerator from "@/pages/DocumentGenerator";
import Documents from "@/pages/Documents";
import StaffDocuments from "@/pages/StaffDocuments";
import Upload from "@/pages/Upload";
import VerifyOrder from "@/pages/VerifyOrder";
import VerifyDocument from "@/pages/VerifyDocument";
import ContentPage from "@/pages/ContentPage";
import "@/App.css";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Redirect({ to }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const target = Object.entries(params).reduce((acc, [k, v]) => acc.replace(`:${k}`, v), to);
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

function AliasRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/biuro-wirtualne(?=\/|$)/, P)}${search}${hash}`} replace />;
}

function OrderRedirect() {
  const { pkg } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const period = (params.get("okres") || params.get("period") || "").toLowerCase();
  const billing = { "1": "monthly", monthly: "monthly", "3": "3", "6": "6", "12": "12", "24": "24" }[period];
  sessionStorage.setItem("tb:pending-order", pkg || "1");
  if (billing) sessionStorage.setItem("tb:pending-billing", billing);
  params.delete("okres");
  params.delete("period");
  const rest = params.toString();
  return <Navigate to={`/${rest ? `?${rest}` : ""}#biuro-wirtualne-cennik`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/operator" element={<Operator />} />
            <Route path="/admin/reset-hasla" element={<AdminResetPassword />} />
            <Route path={`${P}/rodo`} element={<Rodo />} />
            <Route path={`${P}/platnosc`} element={<Payment />} />
            <Route path={`${P}/dokumenty`} element={<Documents />} />
            <Route path={`${P}/dokumenty/:docId`} element={<DocumentGenerator />} />
            <Route path={`${P}/dokumenty-wewnetrzne`} element={<StaffDocuments />} />
            <Route path={`${P}/dokumenty-wewnetrzne/:docId`} element={<DocumentGenerator staff />} />
            <Route path={`${P}/wyslij-podpisany-dokument`} element={<Upload />} />
            <Route path={`${P}/potwierdz-zamowienie`} element={<VerifyOrder />} />
            <Route path={`${P}/potwierdz-dokument`} element={<VerifyDocument />} />
            <Route path={`${P}/legalizacja-dokumentow`} element={<ContentPage slug="legalization" />} />
            <Route path={`${P}/owu`} element={<ContentPage slug="owu" singleColumn />} />
            <Route path={`${P}/wiedza`} element={<Articles />} />
            <Route path={`${P}/wiedza/:slug`} element={<Article />} />
            <Route path={`${P}/zamow-pakiet`} element={<OrderRedirect />} />
            <Route path={`${P}/zamow-pakiet/:pkg`} element={<OrderRedirect />} />
            {/* legacy URLs (old e-mails, indexed pages) → canonical */}
            <Route path="/rodo" element={<Redirect to={`${P}/rodo`} />} />
            <Route path="/platnosc" element={<Redirect to={`${P}/platnosc`} />} />
            <Route path="/dokumenty" element={<Redirect to={`${P}/dokumenty`} />} />
            <Route path="/dokumenty/:docId" element={<Redirect to={`${P}/dokumenty/:docId`} />} />
            <Route path="/wyslij-dokument" element={<Redirect to={`${P}/wyslij-podpisany-dokument`} />} />
            <Route path="/legalizacja-dokumentow" element={<Redirect to={`${P}/legalizacja-dokumentow`} />} />
            <Route path="/owu" element={<Redirect to={`${P}/owu`} />} />
            <Route path="/biuro-wirtualne/wyslij-dokument" element={<Redirect to={`${P}/wyslij-podpisany-dokument`} />} />
            <Route path="/biuro-wirtualne/*" element={<AliasRedirect />} />
            <Route path="/biuro-wirtualne-warszawa" element={<Redirect to="/" />} />
            <Route path="/biuro-wirtualne" element={<Redirect to="/" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
